// Next actions on the chart (old ganttActionsClient_, Actions.gs:357-631): where each bubble sits, the
// popover of one action (Done, deadline, Open task, its own comments), the quick add behind the "+",
// and the completed ones behind the ✓ after the bar.
// Rights come from the bundle (canComment); the server checks them again, with the task's scope.
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  DONE_RECENT,
  isCalendarDate,
  isOverdue,
  isPlanDate,
  MAX_TEXT,
  type ActionCommentItem,
  type ActionItem,
  type GanttBundle,
  type GanttDone,
  type GanttNode,
} from '@pathonline/shared';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { getJson, sendJson, type Note } from '../api.ts';
import { hubDateTime, plural } from '../format.ts';
import { changedValue } from '../inputs.ts';
import { placeBelow, sizeOf, viewport } from '../place.ts';
import { addedDay } from './model.ts';

const enc = encodeURIComponent;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "15 Oct", as the old popover (fmt, Actions.gs:364). */
export function fmtDay(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${+m[3]!} ${MONTHS[+m[2]! - 1]}` : d;
}

const OUT_OF_RANGE = 'Pick a deadline between 2000 and 2100.';

/** Why a copy of an agenda sheet row has no Done or deadline control here (the API refuses both). */
export const sheetNote = (a: ActionItem) => `From the ${a.sheet} agenda sheet: tick Done and change the deadline there.`;

/**
 * What a deadline field saves on blur (the popover, the details panel): the new date, '' = removed, or
 * null = nothing. Compared with what the field can show: a stored "15.10.2026" shows empty, and leaving
 * the field does not erase it. A year half typed ("0202-05-15") is put back, and `setSay` tells why.
 */
export function dueOnBlur(el: HTMLInputElement, stored: string, setSay: (n: Note | null) => void): string | null {
  const shown = isCalendarDate(stored) ? stored : '';
  const v = changedValue(el, shown);
  if (!v || isPlanDate(v)) return v;
  el.value = shown;
  setSay({ bad: true, text: OUT_OF_RANGE });
  return null;
}

/** From one bubble's centre to the next, in px: a 15px bubble and a gap (old 2R+2, Actions.gs:583). */
export const BUBBLE_STEP = 17;

/**
 * Old place() (Actions.gs:579-588), for the bubbles on dates. `xs`: where each would sit, in px, in date
 * order. One closer than a step to a bubble already placed moves a step right, until it is clear.
 */
export function placeBubbles(xs: readonly number[], step = BUBBLE_STEP): number[] {
  const used: number[] = [];
  return xs.map((x0) => {
    let x = x0;
    for (let g = 0; g < 30 && used.some((u) => Math.abs(u - x) < step); g++) x += step;
    used.push(x);
    return x;
  });
}

/**
 * Where the chips after the bar end start, in px (the days-left text, the folder, the bubbles without a date,
 * the "+"; old: they took place() after the date bubbles, Actions.gs:589, 623). After the last date bubble that
 * reaches into them. `width`: theirs.
 */
export function extrasAt(start: number, width: number, bubbles: readonly number[], step = BUBBLE_STEP): number {
  let s = start;
  for (const x of [...bubbles].sort((p, q) => p - q)) if (x + step / 2 > s && x - step / 2 < s + width) s = x + step / 2;
  return s;
}

/** An ACTIVE task with no open action: its "+" is amber (old `stalled`, Actions.gs:620). */
export const isStalled = (n: GanttNode, open: readonly ActionItem[]) => open.length === 0 && (n.status || 'ACTIVE') === 'ACTIVE';

/** Who may lead a new action (the quick add and the details panel): the caller and the people. */
export const leadChoices = (b: GanttBundle) => [...new Set([b.me.person, ...b.people.map((p) => p.name)].filter(Boolean))].sort();

const byDue = (x: ActionItem, y: ActionItem) => (x.due || '9999').localeCompare(y.due || '9999');

/**
 * The bundle with one action as the server answered it (old changed(), Actions.gs:378-391): the chart follows at
 * once, its bubble and its ✓. A reopened action older than the ✓'s newest few is not counted off here; the
 * Gantt read after every save has the count.
 */
export function withAction(b: GanttBundle, a: ActionItem): GanttBundle {
  const rest = (b.openActions[a.nodeId] ?? []).filter((x) => x.id !== a.id);
  const list = a.status === 'done' ? rest : [...rest, a].sort(byDue);
  const openActions = { ...b.openActions };
  if (list.length) openActions[a.nodeId] = list;
  else delete openActions[a.nodeId];
  const d = b.doneActions?.[a.nodeId];
  const listed = !!d?.recent.some((x) => x.id === a.id);
  const others = (d?.recent ?? []).filter((x) => x.id !== a.id);
  const count = (d?.count ?? 0) + (a.status === 'done' ? (listed ? 0 : 1) : listed ? -1 : 0);
  const doneActions = { ...b.doneActions };
  if (count > 0) doneActions[a.nodeId] = { count, recent: a.status === 'done' ? [a, ...others].slice(0, DONE_RECENT) : others };
  else delete doneActions[a.nodeId];
  return { ...b, openActions, doneActions };
}

/** On a bubble (old tip, Actions.gs:606-608). */
export function ActionCard({ a }: { a: ActionItem }) {
  const added = addedDay(a.at);
  return (
    <>
      <div className="g-card-meta">
        <b>Next action</b> · {a.lead}
        {a.due ? ` · deadline ${fmtDay(a.due)}` : added ? ` · no deadline, added ${fmtDay(added)}` : ''}
        {isOverdue(a.due) && <span className="g-card-badge">overdue</span>}
      </div>
      <div className="g-card-desc">{a.text}</div>
      <div className="g-card-more">click for options</div>
    </>
  );
}

/** On the "+" (old tip, Actions.gs:627-629). */
export function PlusCard({ stalled }: { stalled: boolean }) {
  return (
    <>
      <div className="g-card-meta">
        <b>{stalled ? 'No next action yet' : 'Add a next action'}</b>
      </div>
      <div className="g-card-more">click to add — it goes to Todoist and the task’s thread</div>
    </>
  );
}

/** When an action was ticked, and by whom: "done 15 Oct by Todoist". */
export const doneWhen = (a: ActionItem) => {
  const d = addedDay(a.doneAt);
  return `done${d ? ` ${fmtDay(d)}` : ''}${a.doneBy ? ` by ${a.doneBy}` : ''}`;
};

/** On the ✓ after a bar: how many are done, and the newest three. */
export function DoneCard({ d }: { d: GanttDone }) {
  return (
    <>
      <div className="g-card-meta">
        <b>{plural(d.count, 'completed next action')}</b>
      </div>
      {d.recent.slice(0, 3).map((a) => (
        <div key={a.id} className="g-card-desc">
          ✓ {a.text} <span className="g-card-sub">· {doneWhen(a)}</span>
        </div>
      ))}
      <div className="g-card-more">{d.count > 3 ? `+${d.count - 3} more — click to open` : 'click for details'}</div>
    </>
  );
}

/** What the chart opened: one action's popover, the quick add on a task, or its completed actions. `x`, `y`: the bubble's centre. */
export type ActionPop =
  | { kind: 'action'; nodeId: string; action: ActionItem; x: number; y: number }
  | { kind: 'add'; nodeId: string; x: number; y: number }
  | { kind: 'done'; nodeId: string; x: number; y: number };

interface Props {
  b: GanttBundle;
  pop: ActionPop;
  /** Stable (the chart's useCallback): the listeners are set once. */
  onClose: () => void;
  /** "Open task": the details panel. */
  onOpenTask: (nodeId: string) => void;
}

/**
 * An Escape for the popover. Not while a dialog is open over the chart (the thread, a folder): that one
 * closes with this press, the popover under it with the next.
 */
export const popoverEscape = (e: Pick<KeyboardEvent, 'key'>) => e.key === 'Escape' && !document.querySelector('[aria-modal="true"]');

/** By the bubble, inside the window (old openPop, Actions.gs:479-487). A click outside or Escape closes it. */
export function ActionPopover({ b, pop, onClose, onOpenTask }: Props) {
  const box = useRef<HTMLDivElement>(null);
  // Escape cancels: a deadline being typed is not saved by the blur that comes with the close.
  const cancelled = useRef(false);
  const n = b.nodes.find((x) => x.id === pop.nodeId);

  // Below right of the bubble, kept 8px inside the window; above it near the bottom (placeBelow, as the
  // comment preview). On each draw: the comments load after it opens.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const [left, top] = placeBelow({ left: pop.x + 10, top: pop.y - 2, bottom: pop.y + 2 }, sizeOf(el), viewport());
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  });

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const el = box.current;
      if (!el || el.contains(e.target as Node)) return;
      // A deadline being typed is saved first (its blur), as a click elsewhere in the panel does.
      const f = document.activeElement;
      if (f instanceof HTMLElement && el.contains(f)) f.blur();
      onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (!popoverEscape(e)) return;
      e.preventDefault(); // the chart's own Escape (panel, Focus) waits for the next press
      cancelled.current = true;
      onClose();
    };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [onClose]);

  // From the keyboard the next Tab is inside it (the quick add focuses its text box). On opening only (`pop`).
  useEffect(() => {
    if (pop.kind !== 'add') box.current?.focus();
  }, [pop]);

  if (!n) return null;
  // The action as the bundle has it now (a moved deadline), else as it was clicked.
  const a = pop.kind === 'action' ? (b.openActions[n.id]?.find((x) => x.id === pop.action.id) ?? pop.action) : null;
  // Keyed by what was opened: a bubble or "+" opened from the keyboard (no click outside first) starts
  // fresh, not with the draft, message and deadline of the one before (old openPop built it anew).
  const label = a ? `Next action on ${n.label}` : pop.kind === 'done' ? `Completed next actions on ${n.label}` : `New next action on ${n.label}`;
  return (
    <div ref={box} className="g-pop" role="dialog" tabIndex={-1} aria-label={label}>
      {a ? (
        <ActionDetails key={`a:${a.id}`} b={b} n={n} a={a} onClose={onClose} onOpenTask={() => onOpenTask(n.id)} cancelled={cancelled} />
      ) : pop.kind === 'done' ? (
        <DoneList key={`done:${n.id}`} b={b} n={n} onOpenTask={() => onOpenTask(n.id)} />
      ) : (
        <QuickAdd key={`add:${n.id}`} b={b} n={n} onClose={onClose} />
      )}
    </div>
  );
}

/** Gone (closed, or another popover or panel in its place): a save still on its way must not close that one. */
export function useGone() {
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);
  return gone;
}

/**
 * A save of an action, from the popover or the details panel: the answer, or null after the error is
 * shown. `patch` moves the chart at once; the Gantt and the panel's list are then read again. `wait`:
 * busy until they are (the panel's ticks then act on fresh rows).
 */
export function useSave(nodeId: string, setSay: (n: Note | null) => void, wait = false) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const save = async <T,>(f: () => Promise<T>, patch?: (b: GanttBundle, answer: T) => GanttBundle): Promise<T | null> => {
    setBusy(true);
    setSay(null);
    try {
      const answer = await f();
      if (patch) qc.setQueryData<GanttBundle>(['views', 'gantt'], (old) => old && patch(old, answer));
      const fresh = Promise.all([qc.invalidateQueries({ queryKey: ['views'] }), qc.invalidateQueries({ queryKey: ['actions', nodeId] })]);
      if (wait) await fresh;
      return answer;
    } catch (e) {
      setSay({ bad: true, text: (e as Error).message });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { save, busy };
}

/** The add form's fields and its POST: the quick add and the details panel. Each draws its own form. */
export function useAddAction(b: GanttBundle, n: GanttNode, setSay: (n: Note | null) => void, save: ReturnType<typeof useSave>['save']) {
  const leads = leadChoices(b);
  const [text, setText] = useState('');
  // An account with no person starts on the first name, as the select shows it.
  const [lead, setLead] = useState(b.me.person || leads[0] || '');
  const [due, setDue] = useState('');
  const refuse = (why: string) => {
    setSay({ bad: true, text: why });
    return null;
  };
  const add = async (): Promise<ActionItem | null> => {
    if (text.trim().length < 3) return refuse('Describe the action first.');
    if (due && !isPlanDate(due)) return refuse(OUT_OF_RANGE);
    const a = await save(() => sendJson<ActionItem>('POST', `/api/nodes/${enc(n.id)}/actions`, { text, lead, due }), withAction);
    if (a) {
      // Only what was sent: a next action typed while this one was saving stays.
      setText((t) => (t === text ? '' : t));
      setDue((d) => (d === due ? '' : d));
    }
    return a;
  };
  return { leads, text, setText, lead, setLead, due, setDue, add };
}

/** One action (old popAction, Actions.gs:489-545): Done, the deadline, Open task, and its own comments. */
export function ActionDetails({ b, n, a, onClose, onOpenTask, cancelled }: {
  b: GanttBundle; n: GanttNode; a: ActionItem; onClose: () => void; onOpenTask: () => void; cancelled: RefObject<boolean>;
}) {
  const qc = useQueryClient();
  const [say, setSay] = useState<Note | null>(null);
  const [text, setText] = useState('');
  const { save, busy } = useSave(n.id, setSay);
  const gone = useGone();
  const comments = useQuery({ queryKey: ['views', 'action-comments', a.id], queryFn: () => getJson<ActionCommentItem[]>(`/api/actions/${enc(a.id)}/comments`) });
  const can = b.canComment;
  // A sheet copy takes comments, but Done and the deadline belong to the sheet.
  const edit = can && !a.sheet;
  const late = isOverdue(a.due);
  const url = `/api/actions/${enc(a.id)}`;

  // `due`: the deadline as last sent from here; `saved`: as the server last confirmed it. Saves go one
  // after another, in the order they were made.
  const due = useRef(a.due);
  const saved = useRef(a.due);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const field = useRef<HTMLInputElement>(null);
  const setDue = (el: HTMLInputElement, v: string) => {
    if (v === due.current) return;
    due.current = v;
    queue.current = queue.current.then(() =>
      save(() => sendJson<ActionItem>('PATCH', url, { due: v }), withAction).then((r) => {
        if (r) {
          saved.current = r.due;
          setSay({ bad: false, text: v ? `Deadline moved to ${fmtDay(v)}.` : 'Deadline removed.' });
        } else if (due.current === v) {
          // Refused, and nothing newer sent: the field shows the deadline the server has (old dd.value = a.due),
          // not one sent before that was refused too.
          due.current = saved.current;
          el.value = saved.current;
        }
      }),
    );
  };
  // A deadline changed elsewhere shows, unless the field is being edited. The field is not drawn
  // again for its own save: that would end a date being typed.
  useEffect(() => {
    saved.current = a.due;
    const el = field.current;
    if (!el || a.due === due.current || document.activeElement === el) return;
    el.value = a.due;
    due.current = a.due;
  }, [a.due]);

  // The text, edited in place as the deadline: saved on blur and Enter, Escape drops it. `sentText`: as last
  // sent from here; `savedText`: as the server last confirmed it.
  const sentText = useRef(a.text);
  const savedText = useRef(a.text);
  const textField = useRef<HTMLInputElement>(null);
  const setActionText = (el: HTMLInputElement) => {
    const v = el.value.replace(/\s+/g, ' ').trim();
    if (v === sentText.current) return;
    if (v.length < 3) {
      el.value = sentText.current;
      return setSay({ bad: true, text: 'Describe the action first.' });
    }
    sentText.current = v;
    queue.current = queue.current.then(() =>
      save(() => sendJson<ActionItem>('PATCH', url, { text: v }), withAction).then((r) => {
        if (r) {
          savedText.current = r.text;
          setSay({ bad: false, text: r.todoist ? 'Text changed — in Todoist too, and noted in the task’s thread.' : 'Text changed — noted in the task’s thread.' });
        } else if (sentText.current === v) {
          sentText.current = savedText.current;
          el.value = savedText.current;
        }
      }),
    );
  };
  // A text changed elsewhere shows, unless the field is being edited.
  useEffect(() => {
    savedText.current = a.text;
    const el = textField.current;
    if (!el || a.text === sentText.current || document.activeElement === el) return;
    el.value = a.text;
    sentText.current = a.text;
  }, [a.text]);

  // A comment typed below goes with the tick, in its thread note ("Action Done […] — …").
  const done = async () => {
    const t = text.trim();
    if ((await save(() => sendJson<ActionItem>('PATCH', url, { done: true, ...(t ? { comment: t } : {}) }), withAction)) && !gone.current) onClose();
  };
  const post = async (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return setSay({ bad: true, text: 'Write a comment first.' });
    const c = await save(
      () => sendJson<ActionCommentItem>('POST', `${url}/comments`, { text: t }),
      (old) => ({ ...old, commentCounts: { ...old.commentCounts, [n.id]: (old.commentCounts[n.id] ?? 0) + 1 } }),
    );
    if (!c) return;
    qc.setQueryData<ActionCommentItem[]>(['views', 'action-comments', a.id], (old) => [c, ...(old ?? []).filter((x) => x.id !== c.id)]);
    // Only what was sent: a line added while it was on its way stays.
    setText((x) => (x === text ? '' : x));
    setSay({ bad: false, text: 'Posted — also in the task’s comments.' });
  };

  return (
    <>
      <div className="g-pop-h">Next action · {n.label}</div>
      {edit ? (
        <input
          ref={textField}
          className="g-pop-tin"
          defaultValue={a.text}
          maxLength={500}
          title="Change the text"
          aria-label="Next action"
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          onBlur={(e) => {
            if (!cancelled.current) setActionText(e.currentTarget);
          }}
        />
      ) : (
        <div className="g-pop-t">{a.text}</div>
      )}
      <div className="g-pop-m">
        {a.lead}
        {edit ? (
          <>
            {' · deadline '}
            <input
              ref={field}
              type="date"
              defaultValue={a.due}
              title="Change the deadline"
              aria-label="Deadline"
              // Saved on blur and Enter (a click outside blurs it first), as in the details panel; not on
              // each change: typing a day "25" makes the whole date 2026-10-02 first, a save and a thread
              // note of its own. Escape drops it.
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              onBlur={(e) => {
                if (cancelled.current) return;
                const v = dueOnBlur(e.target, due.current, setSay);
                if (v !== null) setDue(e.target, v);
              }}
            />
            {late && <span className="late"> overdue</span>}
          </>
        ) : (
          a.due && (
            <>
              {' · '}
              <span className={late ? 'late' : ''}>
                {late ? 'overdue' : 'deadline'} {fmtDay(a.due)}
              </span>
            </>
          )
        )}
      </div>
      {a.sheet && <div className="g-pop-m">{sheetNote(a)}</div>}
      <div className="g-pop-b">
        {edit && (
          <button type="button" className="btn small" disabled={busy} onClick={() => void done()} title="Mark it done. A comment typed below goes with it, into the task’s thread.">
            ✓ Done{text.trim() ? ' with comment' : ''}
          </button>
        )}
        <button type="button" className="btn small ghost" onClick={onOpenTask}>
          Open task
        </button>
      </div>
      {say && <p className={`g-pop-msg ${say.bad ? 'error' : 'ok'}`} role="status">{say.text}</p>}
      {/* Each one is also a comment on the task, "Next Action […] …": it shows in the thread and the Comments log. */}
      <div className="g-pop-cm">
        {commentList(comments)}
        {can && (
          <form onSubmit={(e) => void post(e)}>
            <textarea placeholder="Comment on this action…" aria-label="Comment on this action" maxLength={MAX_TEXT} value={text} onChange={(e) => setText(e.target.value)} />
            <div className="g-pop-b">
              <button type="submit" className="btn small" disabled={busy}>
                Comment
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

/** An action's own comments, newest first: when on the left, then who, then the text. Drawn in place (no hooks). */
function commentList(q: UseQueryResult<ActionCommentItem[]>) {
  return (
    <div className="g-pop-cl">
      {q.isPending && <p className="g-pop-cn">loading comments…</p>}
      {q.isError && <p className="g-pop-cn error">{q.error.message}</p>}
      {q.data?.length === 0 && <p className="g-pop-cn">No comments on this action yet.</p>}
      {q.data?.map((c) => (
        <div key={c.id} className="g-pop-c">
          <div className="g-pop-cw">
            <span className="g-pop-at">{hubDateTime(c.at)}</span> · {c.author}
          </div>
          <div className="g-pop-cb">{c.body}</div>
        </div>
      ))}
    </div>
  );
}

/** One completed action's comments, read when it is opened in the list. */
function DoneComments({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['views', 'action-comments', id], queryFn: () => getJson<ActionCommentItem[]>(`/api/actions/${enc(id)}/comments`) });
  return commentList(q);
}

/**
 * A task's completed actions, behind the ✓ after its bar: the newest first, as the bundle carries them. A click
 * shows one's own comments, its done note among them; Reopen puts it back on the chart (and in Todoist).
 */
export function DoneList({ b, n, onOpenTask }: { b: GanttBundle; n: GanttNode; onOpenTask: () => void }) {
  const d = b.doneActions?.[n.id];
  const [open, setOpen] = useState('');
  const [say, setSay] = useState<Note | null>(null);
  const { save, busy } = useSave(n.id, setSay);
  const reopen = async (a: ActionItem) => {
    if (await save(() => sendJson<ActionItem>('PATCH', `/api/actions/${enc(a.id)}`, { done: false }), withAction)) {
      setOpen('');
      setSay({ bad: false, text: `Reopened — “${a.text}” is back on the chart.` });
    }
  };
  const older = d ? d.count - d.recent.length : 0;
  return (
    <>
      <div className="g-pop-h">Completed next actions · {n.label}</div>
      {!d && <p className="g-pop-cn">No completed actions left.</p>}
      <div className="g-done-list">
        {d?.recent.map((a) => (
          <div key={a.id} className="g-done-it">
            <button type="button" className="g-done-row" aria-expanded={open === a.id} onClick={() => setOpen(open === a.id ? '' : a.id)}>
              <span className="g-pop-t">✓ {a.text}</span>
              <span className="g-pop-m">
                {a.lead} · {doneWhen(a)}
              </span>
            </button>
            {open === a.id && (
              <div className="g-done-more">
                <DoneComments id={a.id} />
                {a.sheet ? (
                  <div className="g-pop-m">From the {a.sheet} agenda sheet: untick Done there to reopen it.</div>
                ) : (
                  b.canComment && (
                    <button type="button" className="btn small ghost" disabled={busy} onClick={() => void reopen(a)}>
                      Reopen
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {older > 0 && <p className="g-pop-cn">and {plural(older, 'older one')}</p>}
      <div className="g-pop-b">
        <button type="button" className="btn small ghost" onClick={onOpenTask}>
          Open task
        </button>
      </div>
      {say && <p className={`g-pop-msg ${say.bad ? 'error' : 'ok'}`} role="status">{say.text}</p>}
    </>
  );
}

/** The quick add behind the "+" (old popAdd, Actions.gs:546-562): text, lead, deadline. */
export function QuickAdd({ b, n, onClose }: { b: GanttBundle; n: GanttNode; onClose: () => void }) {
  const [say, setSay] = useState<Note | null>(null);
  const { save, busy } = useSave(n.id, setSay);
  const { leads, text, setText, lead, setLead, due, setDue, add } = useAddAction(b, n, setSay, save);
  const gone = useGone();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if ((await add()) && !gone.current) onClose();
  };

  return (
    <form onSubmit={(e) => void submit(e)}>
      <div className="g-pop-h">New next action · {n.label}</div>
      <input className="g-pop-in" placeholder="What is the next step?" aria-label="Next action" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      <div className="g-pop-row">
        <select value={lead} onChange={(e) => setLead(e.target.value)} title="Lead" aria-label="Lead">
          {leads.map((p) => <option key={p}>{p}</option>)}
        </select>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} title="Deadline" aria-label="Deadline" />
      </div>
      <div className="g-pop-b">
        <button type="submit" className="btn small" disabled={busy}>
          {busy ? '…' : 'Add'}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      {say && <p className={`g-pop-msg ${say.bad ? 'error' : 'ok'}`} role="status">{say.text}</p>}
    </form>
  );
}
